/**
 * Stage 4 Configuration — advisory tank logistics UI.
 * Does NOT solve stoichiometry. Uses shared tankLogistics module (P3).
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  analyzeTankLogistics,
  type LogisticsSalt,
  type TankLogisticsIssue,
} from '../../utils/tankLogistics';

interface SaltCompatibilityAnalyzerProps {
  salts: LogisticsSalt[];
  onCompatibilityAnalysis: (analysis: {
    partA: string[];
    partB: string[];
    tankById: Record<string, 'A' | 'B'>;
    issues: TankLogisticsIssue[];
    recommendations: string[];
    recommendAB: boolean;
  }) => void;
  /** Suggest stock type when logistics recommends A/B */
  onRecommendStockType?: (type: 'single' | 'partAB') => void;
}

const SaltCompatibilityAnalyzer: React.FC<SaltCompatibilityAnalyzerProps> = ({
  salts,
  onCompatibilityAnalysis,
  onRecommendStockType,
}) => {
  const analysis = React.useMemo(
    () => analyzeTankLogistics(salts || []),
    [salts]
  );

  // Notify parent when analysis changes (stable by salt id list)
  const saltKey = (salts || []).map((s) => s.id).join(',');
  React.useEffect(() => {
    onCompatibilityAnalysis({
      partA: analysis.partA,
      partB: analysis.partB,
      tankById: analysis.tankById,
      issues: analysis.issues,
      recommendations: analysis.recommendations,
      recommendAB: analysis.recommendAB,
    });
    if (onRecommendStockType) {
      onRecommendStockType(analysis.recommendAB ? 'partAB' : 'single');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saltKey captures salt set
  }, [saltKey, analysis.recommendAB]);

  const getSeverityIcon = (severity: 'warning' | 'error') => {
    if (severity === 'error') return <ErrorIcon color="error" fontSize="small" />;
    return <WarningIcon color="warning" fontSize="small" />;
  };

  const nameById = React.useMemo(() => {
    const m = new Map<string, string>();
    (salts || []).forEach((s) => m.set(s.id, s.name));
    return m;
  }, [salts]);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tank logistics (advisory)
        </Typography>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            This is <strong>not</strong> the nutrient matrix solver. Pair risks and A/B
            assignment only help you prepare concentrated stocks without precipitation.
            Salt <em>amounts</em> still come from water + crop targets + salt composition
            in the Professional Calculations step.
          </Typography>
        </Alert>

        {analysis.issues.length === 0 ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon />
              <Typography variant="body2">
                No major pair risks detected. Single stock is acceptable; A/B remains optional.
              </Typography>
            </Box>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {analysis.issues.length} logistics issue(s).{' '}
              {analysis.recommendAB
                ? 'Part A + Part B stock is recommended.'
                : 'Review recommendations below.'}
            </Typography>
          </Alert>
        )}

        {analysis.issues.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Salt combination</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Recommendation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analysis.issues.map((issue, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Tooltip title={issue.severity}>
                        {getSeverityIcon(issue.severity)}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {issue.saltA} + {issue.saltB}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{issue.reason}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{issue.recommendation}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Proposed A/B assignment preview */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Proposed tank assignment (same rules as A/B separation step)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip
              color="primary"
              size="small"
              label={`Tank A (${analysis.partA.length}): ${
                analysis.partA.map((id) => nameById.get(id) || id).join(', ') || '—'
              }`}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              color="secondary"
              size="small"
              label={`Tank B (${analysis.partB.length}): ${
                analysis.partB.map((id) => nameById.get(id) || id).join(', ') || '—'
              }`}
            />
          </Box>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Recommendations
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {analysis.recommendations.map((rec, index) => (
            <Chip
              key={index}
              label={rec}
              variant="outlined"
              size="small"
              color="primary"
              sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5 }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SaltCompatibilityAnalyzer;
